import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Callout, SelectField, Spinner, Tab, TabGroup } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { userStore } from '../auth/store';
import { DetailPair, SectionShell } from '../features/workers/components/SectionShell';
import { HrBanking, HrSuperFund, HrTax } from '../features/workers/components/HrPayrollSections';
import {
  getBusinessRecords, getHrWorker, getPayGroups, getSuperSchemes, getWorkerAssignments, getWorkerPayrollDetails,
  saveEmployment, saveSuperannuation,
  type HrWorkerPersonal, type WorkerAssignments, type WorkerPayrollDetails, type WorkerReference,
} from '../features/workers/workerDirectoryApi';
import { useWorkerPersonalMemory } from '../features/workers/WorkerPersonalMemory';
import { WorkerLeavePanel } from '../features/workers/WorkerLeavePanel';
import { observer } from 'mobx-react-lite';

const TABS = [
  { slug: 'personal', label: 'Personal' },
  { slug: 'employment', label: 'Employment' },
  { slug: 'superannuation', label: 'Superannuation' },
  { slug: 'pay', label: 'Pay' },
  { slug: 'tax', label: 'Tax' },
  { slug: 'banking', label: 'Banking' },
  { slug: 'leave', label: 'Leave' },
  { slug: 'documents', label: 'Documents' },
  { slug: 'notes', label: 'Notes' },
  { slug: 'activity', label: 'Activity' },
] as const;

const label = (value: string | null | undefined): string => value?.trim() || '—';
const name = (person: HrWorkerPersonal): string =>
  [person.firstName, person.lastName].filter(Boolean).join(' ').trim() || 'Worker record';
const referenceName = (references: WorkerReference[], id: string | undefined): string =>
  references.find((item) => item.id === id)?.name ?? '—';
const canEdit = (): boolean => userStore.roles.includes('ADMIN') || userStore.roles.includes('PAYROLL_MANAGER');
const canPostOpening = (): boolean => userStore.roles.includes('PAYROLL_MANAGER') || userStore.roles.includes('PAYROLL_OPERATOR');

const Personal = ({ person }: { person: HrWorkerPersonal }) => (
  <SectionShell title="Person details" description="Read-only details from Humanforce HR.">
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
      <DetailPair label="Title" value={label(person.title)} />
      <DetailPair label="First name" value={label(person.firstName)} />
      <DetailPair label="Last name" value={label(person.lastName)} />
      <DetailPair label="Preferred name" value={label(person.preferredName)} />
      <DetailPair label="Employee number" value={label(person.employeeNumber)} />
      <DetailPair label="Humanforce HR ID" value={label(person.autoIncrementIntellihrId)} />
      <DetailPair label="HR Person UUID" value={person.personId} />
      <DetailPair label="Date of birth" value={label(person.dateOfBirth?.slice(0, 10))} />
      <DetailPair label="Gender" value={label(person.gender)} />
    </div>
  </SectionShell>
);

interface AssignmentPanelProps {
  personId: string;
  assignments: WorkerAssignments;
  onSaved: (next: WorkerAssignments) => void;
}

const Employment = ({ personId, assignments, onSaved }: AssignmentPanelProps) => {
  const [businessRecords, setBusinessRecords] = useState<WorkerReference[]>([]);
  const [payGroups, setPayGroups] = useState<WorkerReference[]>([]);
  const [editing, setEditing] = useState(false);
  const [businessRecordId, setBusinessRecordId] = useState(assignments.employment?.businessRecordId ?? '');
  const [payGroupId, setPayGroupId] = useState(assignments.employment?.payGroupId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [clearRequired, setClearRequired] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { void getBusinessRecords().then((result) => { if (result.ok) setBusinessRecords(result.data ?? []); }); }, []);
  useEffect(() => {
    if (!businessRecordId) { setPayGroups([]); return; }
    let ignore = false;
    void getPayGroups(businessRecordId).then((result) => { if (!ignore) setPayGroups(result.ok ? result.data ?? [] : []); });
    return () => { ignore = true; };
  }, [businessRecordId]);

  const reset = () => {
    setBusinessRecordId(assignments.employment?.businessRecordId ?? '');
    setPayGroupId(assignments.employment?.payGroupId ?? '');
    setClearRequired(false);
    setError(null);
    setEditing(false);
  };
  const save = async (confirmClear: boolean) => {
    if (!businessRecordId || !payGroupId) { setError('Select a Business Record and Pay Group.'); return; }
    setSaving(true);
    const result = await saveEmployment(personId, businessRecordId, payGroupId, confirmClear);
    setSaving(false);
    if (result.ok && result.data) { onSaved(result.data); setEditing(false); setClearRequired(false); setError(null); }
    else if (result.status === 409 && result.code === 'clear_super_required' && !confirmClear) { setClearRequired(true); setError(null); }
    else setError(result.message ?? 'Could not save Employment.');
  };

  return <SectionShell title="Employment" action={canEdit() && !editing && <Button variant="secondary" label="Edit" onClick={() => setEditing(true)} />}>
    {editing ? <div className="mgo-stack mgo-gap-l">
      <SelectField label="Business Record" value={businessRecordId || null} options={businessRecords.map((item) => ({ value: item.id, label: item.name }))}
        onChange={(value) => { setBusinessRecordId(String(value ?? '')); setPayGroupId(''); setClearRequired(false); }} />
      <SelectField label="Pay Group" value={payGroupId || null} disabled={!businessRecordId}
        options={payGroups.map((item) => ({ value: item.id, label: item.name }))} onChange={(value) => setPayGroupId(String(value ?? ''))} />
      {clearRequired && <Callout tone="caution" title="Clear incompatible Superannuation?">
        <div className="mgo-stack mgo-gap-s">
          <p>The saved Super Scheme does not apply to the selected Business Record. Confirming will clear that assignment when Employment is saved.</p>
          <div><Button variant="primary" label="Confirm and clear Superannuation" disabled={saving} onClick={() => void save(true)} /></div>
        </div>
      </Callout>}
      {error && <Callout tone="critical" title="Could not save Employment">{error}</Callout>}
      <div className="mgo-cluster mgo-gap-s">
        {!clearRequired && <Button variant="primary" label="Save" disabled={saving} onClick={() => void save(false)} />}
        <Button variant="secondary" label="Cancel" disabled={saving} onClick={reset} />
      </div>
    </div> : <div className="mgo-stack mgo-gap-l">
      <DetailPair label="Business Record" value={referenceName(businessRecords, assignments.employment?.businessRecordId)} />
      <DetailPair label="Pay Group" value={referenceName(payGroups, assignments.employment?.payGroupId)} />
    </div>}
  </SectionShell>;
};

const Superannuation = ({ personId, assignments, onSaved }: AssignmentPanelProps) => {
  const businessRecordId = assignments.employment?.businessRecordId;
  const [schemes, setSchemes] = useState<WorkerReference[]>([]);
  const [editing, setEditing] = useState(false);
  const [schemeId, setSchemeId] = useState(assignments.superannuation?.superSchemeId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!businessRecordId) { setSchemes([]); return; }
    let ignore = false;
    void getSuperSchemes(businessRecordId).then((result) => { if (!ignore) setSchemes(result.ok ? result.data ?? [] : []); });
    return () => { ignore = true; };
  }, [businessRecordId]);
  useEffect(() => { setSchemeId(assignments.superannuation?.superSchemeId ?? ''); }, [assignments.superannuation?.superSchemeId]);

  const save = async () => {
    if (!schemeId) { setError('Select a Super Scheme.'); return; }
    setSaving(true);
    const result = await saveSuperannuation(personId, schemeId);
    setSaving(false);
    if (result.ok && result.data) { onSaved(result.data); setEditing(false); setError(null); }
    else setError(result.message ?? 'Could not save Superannuation.');
  };
  return <SectionShell title="Superannuation Scheme" description="Managed in Roadrunner." action={canEdit() && businessRecordId && !editing && <Button variant="secondary" label="Edit" onClick={() => setEditing(true)} />}>
    {!businessRecordId ? <Callout tone="caution" title="Employment required">Save Employment before selecting a Super Scheme.</Callout>
      : editing ? <div className="mgo-stack mgo-gap-l">
        <SelectField label="Super Scheme" value={schemeId || null} options={schemes.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(value) => setSchemeId(String(value ?? ''))} />
        {error && <Callout tone="critical" title="Could not save Superannuation">{error}</Callout>}
        <div className="mgo-cluster mgo-gap-s">
          <Button variant="primary" label="Save" disabled={saving} onClick={() => void save()} />
          <Button variant="secondary" label="Cancel" disabled={saving} onClick={() => { setSchemeId(assignments.superannuation?.superSchemeId ?? ''); setEditing(false); setError(null); }} />
        </div>
      </div> : <DetailPair label="Super Scheme" value={referenceName(schemes, assignments.superannuation?.superSchemeId)} />}
  </SectionShell>;
};

export const WorkerRecordPage = observer(() => {
  const [searchParams] = useSearchParams();
  const personId = searchParams.get('worker') ?? '';
  return <WorkerRecordContent key={`${window.location.host}:${userStore.authEpoch}:${personId}`} personId={personId} />;
});

const WorkerRecordContent = ({ personId }: { personId: string }) => {
  const navigate = useNavigate();
  const { '*': wildcard } = useParams();
  const activeSlug = TABS.some((tab) => tab.slug === wildcard) ? wildcard! : 'personal';
  const memory = useWorkerPersonalMemory();
  const cached = personId ? memory.find(personId) : undefined;
  const [person, setPerson] = useState<HrWorkerPersonal | null>(cached ?? null);
  const [assignments, setAssignments] = useState<WorkerAssignments | null>(null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [confirmedMissing, setConfirmedMissing] = useState(false);
  const [payroll, setPayroll] = useState<WorkerPayrollDetails | null>(null);
  const [payrollStatus, setPayrollStatus] = useState<'idle' | 'loading' | 'ready' | 'denied' | 'error'>('idle');
  const payrollStarted = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!personId || confirmedMissing) { setLoading(false); return; }
    if (cached) { setPerson(cached); setLoading(false); return; }
    let ignore = false;
    setLoading(true);
    void getHrWorker(personId).then((result) => {
      if (ignore) return;
      if (result.ok && result.data) setPerson(result.data);
      else setError(result.message ?? 'Could not load this worker.');
      setLoading(false);
    });
    return () => { ignore = true; };
  }, [personId, cached, confirmedMissing]);
  useEffect(() => {
    // The Personal tab already has the HR details from the list. Assignment reads
    // revalidate HR on the server, so defer them until an assignment tab is opened.
    if (!personId || !person || assignments || (activeSlug !== 'employment' && activeSlug !== 'superannuation')) return;
    let ignore = false;
    void getWorkerAssignments(personId).then((result) => {
      if (ignore) return;
      if (result.ok) {
        setAssignments(result.data ?? { employment: null, superannuation: null });
        setError(null);
      }
      else {
        if (result.status === 404) {
          setConfirmedMissing(true);
          memory.forget(personId);
          setPerson(null);
        }
        setError(result.message ?? 'Could not load payroll assignments.');
      }
    });
    return () => { ignore = true; };
  }, [personId, person, assignments, activeSlug, memory.forget]);

  useEffect(() => {
    if (!person || !personId || !['banking', 'tax', 'superannuation'].includes(activeSlug)
      || payrollStarted.current || payrollStatus !== 'idle') return;
    payrollStarted.current = true;
    setPayrollStatus('loading');
    void getWorkerPayrollDetails(personId).then((result) => {
      if (!mounted.current) return;
      if (result.ok && result.data?.personId === personId) {
        setPayroll(result.data);
        setPayrollStatus('ready');
      } else if (result.status === 403) setPayrollStatus('denied');
      else {
        if (result.status === 404) { setConfirmedMissing(true); memory.forget(personId); setPerson(null); }
        setPayrollStatus('error');
      }
    });
  }, [person, personId, activeSlug, payrollStatus, memory.forget]);

  const retryPayroll = () => { payrollStarted.current = false; setPayroll(null); setPayrollStatus('idle'); };
  const sensitivePanel = (render: (value: WorkerPayrollDetails) => React.ReactNode) => {
    if (payrollStatus === 'loading' || payrollStatus === 'idle')
      return <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> Loading HR payroll details…</div>;
    if (payrollStatus === 'denied') return <Callout tone="caution" title="Access denied">You cannot view these HR details.</Callout>;
    if (payrollStatus === 'error' || !payroll)
      return <Callout tone="critical" title="Could not load HR details"><Button variant="secondary" label="Retry" onClick={retryPayroll} /></Callout>;
    return render(payroll);
  };

  if (!personId) return <Callout tone="caution" title="No worker selected">Open a worker from the Workers list.</Callout>;
  return <>
    <PageTitle title={person ? name(person) : 'Worker record'} actions={<Button variant="tertiary" label="Back to workers" onClick={() => navigate('/workers/worker-list')} />} />
    {error && <Callout tone="critical" title="Couldn't load worker">{error}</Callout>}
    {loading && <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> Loading worker…</div>}
    {person && <TabGroup value={activeSlug} onValueChange={(value) => navigate(`/workers/worker-record/${value}?worker=${encodeURIComponent(personId)}`)}>
      {TABS.map((tab) => <Tab key={tab.slug} value={tab.slug} label={tab.label}>
        {tab.slug === 'personal' ? <Personal person={person} />
          : tab.slug === 'employment' && assignments ? <Employment personId={personId} assignments={assignments} onSaved={setAssignments} />
          : tab.slug === 'superannuation' ? <div className="mgo-stack mgo-gap-l">
            {sensitivePanel((value) => <HrSuperFund superFund={value.superFund} />)}
            {assignments && <Superannuation personId={personId} assignments={assignments} onSaved={setAssignments} />}
          </div>
          : tab.slug === 'tax' ? sensitivePanel((value) => <HrTax tax={value.tax} />)
          : tab.slug === 'banking' ? sensitivePanel((value) => <HrBanking banking={value.banking} />)
          : tab.slug === 'leave' ? <WorkerLeavePanel personId={personId} canManage={canEdit()}
            canCorrectOpening={userStore.roles.includes('PAYROLL_MANAGER')} canPostOpening={canPostOpening()} />
          : null}
      </Tab>)}
    </TabGroup>}
  </>;
};
